/**
 * Measure text dimensions using a temporary canvas context.
 * @param {string} text
 * @param {string} fontFamily
 * @param {number} fontSize
 * @param {boolean} bold
 * @param {boolean} italic
 * @param {number} letterSpacing
 * @param {number} lineHeight
 * @returns {{ width: number, height: number }}
 */
export function measureText(text, fontFamily, fontSize, bold, italic, letterSpacing, lineHeight) {
	const canvas = document.createElement('canvas')
	const ctx = canvas.getContext('2d')
	const weight = bold ? 'bold' : 'normal'
	const style = italic ? 'italic' : 'normal'
	ctx.font = `${style} ${weight} ${fontSize}px ${fontFamily}`

	const lines = (text || '').split('\n')
	const lh = (lineHeight || 1.2) * fontSize
	let maxWidth = 0
	for (const line of lines) {
		let w = ctx.measureText(line).width
		if (letterSpacing && line.length > 1) {
			w += letterSpacing * (line.length - 1)
		}
		if (w > maxWidth) maxWidth = w
	}
	const height = Math.max(lh, lines.length * lh)
	return { width: Math.ceil(maxWidth) || fontSize, height: Math.ceil(height) || fontSize }
}

/**
 * Measure per-character positions within a text object. Returns an array of { char, x, y, width, height } for each non-whitespace character, accounting for multi-line text, letterSpacing, and textAlign.
 */
export function measureCharPositions(text, fontFamily, fontSize, bold, italic, letterSpacing, lineHeight, textAlign) {
	const canvas = document.createElement('canvas')
	const ctx = canvas.getContext('2d')
	const weight = bold ? 'bold' : 'normal'
	const style = italic ? 'italic' : 'normal'
	ctx.font = `${style} ${weight} ${fontSize}px ${fontFamily}`

	const lines = (text || '').split('\n')
	const lh = (lineHeight || 1.2) * fontSize

	// First pass: measure each line's total width
	const lineWidths = lines.map(line => {
		let w = ctx.measureText(line).width
		if (letterSpacing && line.length > 1) {
			w += letterSpacing * (line.length - 1)
		}
		return w
	})
	const maxWidth = Math.max(...lineWidths)

	// Second pass: compute per-character positions
	const positions = []
	for (let i = 0; i < lines.length; i++) {
		const line = lines[i]
		const lineWidth = lineWidths[i]
		const yOffset = i * lh

		// textAlign offset
		let xStart = 0
		if (textAlign === 'center') {
			xStart = (maxWidth - lineWidth) / 2
		} else if (textAlign === 'right') {
			xStart = maxWidth - lineWidth
		}

		let xCursor = xStart
		for (let j = 0; j < line.length; j++) {
			const char = line[j]
			const charWidth = ctx.measureText(char).width
			const charHeight = lh

			positions.push({
				char,
				x: xCursor,
				y: yOffset,
				width: Math.ceil(charWidth) || fontSize,
				height: Math.ceil(charHeight) || fontSize,
			})

			xCursor += charWidth + (letterSpacing || 0)
		}
	}

	return positions
}
